import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Divider,
    Grid,
    Chip,
    CircularProgress,
    InputAdornment,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslate, useGetIdentity } from 'react-admin';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

interface PaymentSettingsData {
    id: number | null;
    companyId: number;
    paypalEnabled: boolean;
    paypalClientId: string | null;
    paypalSecretMasked: string | null;
    paypalSandbox: boolean;
    paypalConfigured: boolean;
    stripeEnabled: boolean;
    stripePublicKey: string | null;
    stripeSecretKeyMasked: string | null;
    stripeTestMode: boolean;
    stripeConfigured: boolean;
    defaultCurrency: string;
    allowFreeEnrollment: boolean;
    enabledProviders: string[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
    <div role="tabpanel" hidden={value !== index} {...other}>
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

const PaymentSettings: React.FC = () => {
    const translate = useTranslate();
    const { data: identity } = useGetIdentity();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const [settings, setSettings] = useState<PaymentSettingsData | null>(null);

    // Form state
    const [paypalEnabled, setPaypalEnabled] = useState(false);
    const [paypalClientId, setPaypalClientId] = useState('');
    const [paypalSecret, setPaypalSecret] = useState('');
    const [paypalSandbox, setPaypalSandbox] = useState(true);
    const [showPaypalSecret, setShowPaypalSecret] = useState(false);

    const [stripeEnabled, setStripeEnabled] = useState(false);
    const [stripePublicKey, setStripePublicKey] = useState('');
    const [stripeSecretKey, setStripeSecretKey] = useState('');
    const [stripeTestMode, setStripeTestMode] = useState(true);
    const [showStripeSecret, setShowStripeSecret] = useState(false);

    const [defaultCurrency, setDefaultCurrency] = useState('EUR');
    const [allowFreeEnrollment, setAllowFreeEnrollment] = useState(true);

    const companyId = identity?.companyId;

    useEffect(() => {
        if (companyId) {
            loadSettings();
        }
    }, [companyId]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${SSO_URL}/api/payment-settings/${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const s = data.settings;
                setSettings(s);

                // Populate form
                setPaypalEnabled(s.paypalEnabled || false);
                setPaypalClientId(s.paypalClientId || '');
                setPaypalSecret(s.paypalSecretMasked || '');
                setPaypalSandbox(s.paypalSandbox ?? true);

                setStripeEnabled(s.stripeEnabled || false);
                setStripePublicKey(s.stripePublicKey || '');
                setStripeSecretKey(s.stripeSecretKeyMasked || '');
                setStripeTestMode(s.stripeTestMode ?? true);

                setDefaultCurrency(s.defaultCurrency || 'EUR');
                setAllowFreeEnrollment(s.allowFreeEnrollment ?? true);
            }
        } catch (error) {
            console.error('Failed to load payment settings:', error);
            setSnackbar({
                open: true,
                message: 'Fehler beim Laden der Einstellungen',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('token');

            const payload: any = {
                paypalEnabled,
                paypalClientId: paypalClientId || null,
                paypalSandbox,
                stripeEnabled,
                stripePublicKey: stripePublicKey || null,
                stripeTestMode,
                defaultCurrency,
                allowFreeEnrollment,
            };

            // Only include secrets if they were changed (not masked values)
            if (paypalSecret && !paypalSecret.startsWith('••••')) {
                payload.paypalSecret = paypalSecret;
            }
            if (stripeSecretKey && !stripeSecretKey.startsWith('••••')) {
                payload.stripeSecretKey = stripeSecretKey;
            }

            const response = await fetch(`${SSO_URL}/api/payment-settings/${companyId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const data = await response.json();
                setSettings(data.settings);
                setSnackbar({
                    open: true,
                    message: 'Einstellungen gespeichert',
                    severity: 'success',
                });
                // Reload to get updated masked values
                loadSettings();
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            console.error('Failed to save payment settings:', error);
            setSnackbar({
                open: true,
                message: 'Fehler beim Speichern',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon /> Zahlungseinstellungen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Konfigurieren Sie die Zahlungsanbieter für Ihren Shop.
            </Typography>

            {/* Status Overview */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                    icon={settings?.paypalConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={`PayPal: ${settings?.paypalConfigured ? 'Konfiguriert' : 'Nicht konfiguriert'}`}
                    color={settings?.paypalConfigured ? 'success' : 'default'}
                    variant="outlined"
                />
                <Chip
                    icon={settings?.stripeConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={`Stripe: ${settings?.stripeConfigured ? 'Konfiguriert' : 'Nicht konfiguriert'}`}
                    color={settings?.stripeConfigured ? 'success' : 'default'}
                    variant="outlined"
                />
            </Box>

            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                        <Tab label="PayPal" icon={<PaymentIcon />} iconPosition="start" />
                        <Tab label="Stripe / Kreditkarte" icon={<CreditCardIcon />} iconPosition="start" />
                        <Tab label="Allgemein" />
                    </Tabs>
                </Box>

                <CardContent>
                    {/* PayPal Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={paypalEnabled}
                                    onChange={(e) => setPaypalEnabled(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="PayPal aktivieren"
                        />

                        {paypalEnabled && (
                            <Box sx={{ mt: 3 }}>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Erstellen Sie eine App im{' '}
                                    <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer">
                                        PayPal Developer Dashboard
                                    </a>{' '}
                                    um Client ID und Secret zu erhalten.
                                </Alert>

                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Client ID"
                                            value={paypalClientId}
                                            onChange={(e) => setPaypalClientId(e.target.value)}
                                            placeholder="AX..."
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Client Secret"
                                            type={showPaypalSecret ? 'text' : 'password'}
                                            value={paypalSecret}
                                            onChange={(e) => setPaypalSecret(e.target.value)}
                                            placeholder="EL..."
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPaypalSecret(!showPaypalSecret)} edge="end">
                                                            {showPaypalSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText="Lassen Sie das Feld leer, um den bestehenden Wert beizubehalten"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={paypalSandbox}
                                                    onChange={(e) => setPaypalSandbox(e.target.checked)}
                                                    color="warning"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography>Sandbox/Testmodus</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Aktivieren Sie dies für Testzahlungen. Deaktivieren Sie es für echte Zahlungen.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Stripe Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={stripeEnabled}
                                    onChange={(e) => setStripeEnabled(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Stripe / Kreditkarte aktivieren"
                        />

                        {stripeEnabled && (
                            <Box sx={{ mt: 3 }}>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Finden Sie Ihre API-Schlüssel im{' '}
                                    <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
                                        Stripe Dashboard
                                    </a>.
                                </Alert>

                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Publishable Key (öffentlich)"
                                            value={stripePublicKey}
                                            onChange={(e) => setStripePublicKey(e.target.value)}
                                            placeholder="pk_test_..."
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Secret Key (geheim)"
                                            type={showStripeSecret ? 'text' : 'password'}
                                            value={stripeSecretKey}
                                            onChange={(e) => setStripeSecretKey(e.target.value)}
                                            placeholder="sk_test_..."
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowStripeSecret(!showStripeSecret)} edge="end">
                                                            {showStripeSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText="Lassen Sie das Feld leer, um den bestehenden Wert beizubehalten"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={stripeTestMode}
                                                    onChange={(e) => setStripeTestMode(e.target.checked)}
                                                    color="warning"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography>Testmodus</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Verwenden Sie Test-API-Schlüssel (pk_test_, sk_test_) für Testzahlungen.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </TabPanel>

                    {/* General Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Standard-Währung</InputLabel>
                                    <Select
                                        value={defaultCurrency}
                                        onChange={(e) => setDefaultCurrency(e.target.value)}
                                        label="Standard-Währung"
                                    >
                                        <MenuItem value="EUR">EUR - Euro</MenuItem>
                                        <MenuItem value="USD">USD - US-Dollar</MenuItem>
                                        <MenuItem value="CHF">CHF - Schweizer Franken</MenuItem>
                                        <MenuItem value="GBP">GBP - Britisches Pfund</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={allowFreeEnrollment}
                                            onChange={(e) => setAllowFreeEnrollment(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography>Kostenlose Einschreibungen erlauben</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Ermöglicht Teilnehmern, sich für kostenlose Kurse ohne Zahlungsvorgang anzumelden.
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={saveSettings}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            {saving ? 'Speichern...' : 'Einstellungen speichern'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PaymentSettings;
